// @flow
import * as campaignApi from '../modules/api/campaign';
import * as customerSignupApi from '../modules/api/customer-signup';
import * as productNotesApi from '../modules/api/product-note';
import * as productApi from '../modules/api/product';
import * as reportOrdersApi from '../modules/api/report-orders';
import * as returnLabelApi from '../modules/api/return-label';
import * as scanApi from '../modules/api/scan';
import * as userApi from '../modules/api/user';
import * as at from './action-types';
import * as resources from '../modules/api/resources';
import { makeScan, type Scan } from '../modules/scan';
import {
  type Product,
  type ProductsQueryResponse,
  allocationStrategy,
  releaseDateFromStockRecord,
} from '../modules/product';
import * as inventory from './inventory';
import type { ProductNote } from '../modules/product-note';
import type { ManagedUser } from '../modules/user';
import type { BarcodeQuery } from '../modules/barcode';
import type { ThunkAction, Dispatch, GetState } from '../store';
import type { Action as NewOrderAction } from './new-order';
import type { Action as QuikOrderHistoryAction } from './quik-order-history';
import type { ReturnLabel } from '../modules/return-label';
import type { CustomerSignupFormValues } from '../modules/customer-signup';
import type { ReportOrdersFormValues } from '../modules/report-orders';
import type {
  RequestResourceAction,
  ReceiveResourceAction,
  FailToReceiveResourceAction,
} from './resource-actions';

import type { CampaignDraft } from 'modules/campaign';
import { type FSA } from 'modules/fsa';
import { type AbstractCatalog } from 'modules/abstract-catalog';

export * from './resource-actions';

export const fetchManagedUsers = userApi.managedUsers;

export type UpdateResource<R> = (
  resource: R,
  updates: $Shape<R>,
) => ThunkAction;
export type UpdateResourceById<R> = (
  resource: { id: string },
  updates: $Shape<R>,
) => ThunkAction;

export type SuperficallyUpdateUserIsDisabled = {
  type: typeof at.UPDATE_USER_IS_DISABLED,
  payload: {
    id: string,
  },
};

export const superficallyUpdateUserIsDisabled = (
  id: string,
): SuperficallyUpdateUserIsDisabled => ({
  type: at.UPDATE_USER_IS_DISABLED,
  payload: {
    id,
  },
});

export const updateUserIsDisabled =
  (
    user: ManagedUser,
    isDisabled: boolean,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<Response>) =>
  (dispatch: Dispatch, _): any => {
    dispatch(superficallyUpdateUserIsDisabled(user.id));
    dispatch(
      userApi.updateUserIsDisabled(
        user,
        // $FlowFixMe[incompatible-call]
        { user: resources.user.serialized({ isDisabled }) },
      ),
    ).catch(() => dispatch(superficallyUpdateUserIsDisabled(user.id)));
  };

export const updateUser: UpdateResource<ManagedUser> = (user, updates) =>
  userApi.updateUser(
    user,
    /* ⚠️ maybe not the place for this? */
    {
      // $FlowFixMe[incompatible-call]
      user: resources.user.serialized(updates),
    },
  );

export const createManagedUser = (
  params: ManagedUser,
): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  // $FlowFixMe[incompatible-call]
  userApi.createManagedUser({ user: resources.managedUser.serialized(params) });

export const createNewCampaign = (
  params: CampaignDraft,
): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  campaignApi.createCampaign({
    // $FlowFixMe[incompatible-call]
    campaign: resources.campaign.serialized(params),
  });

export const editCampaign = (
  campaign: CampaignDraft,
  updates: any,
): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  // $FlowFixMe[incompatible-call]
  campaignApi.updateCampaign(campaign, {
    // $FlowFixMe[incompatible-call]
    campaign: resources.updateCampaign.serialized(updates),
  });

export const deleteCampaign = (
  id: string,
): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  campaignApi.deleteCampaign(id);

export const { deleteUser } = userApi;

export const fetchProductNotes = productNotesApi.productNotes;
export const createProductNote = (params: {
  productNumber: string,
  catalogKey: string,
  customerNumber: ?string,
  variationCode: ?string,
  text: string,
}): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  productNotesApi.createProductNote({
    // $FlowFixMe[incompatible-call]
    note: resources.productNote.serialized(params),
  });

export const updateProductNote: UpdateResourceById<ProductNote> = (
  note,
  updates,
) =>
  productNotesApi.updateProductNote(note, {
    // $FlowFixMe[incompatible-call]
    note: resources.productNote.serialized(updates),
  });

export const deleteProductNote = ({
  id,
}: {
  id: string,
}): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  productNotesApi.deleteProductNote(id);

export const createReturnLabel = (
  params: ReturnLabel,
): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  returnLabelApi.createReturnLabel({
    // $FlowFixMe[not-an-object]
    ...resources.returnLabel.serialized(
      // $FlowFixMe[incompatible-call]
      params,
    ),
  });

export const fetchExternalPrices = productApi.externalPrices;

type LogInAction = FSA<
  typeof at.LOG_IN,
  $ReadOnly<{|
    username: string,
  |}>,
>;

export const logIn = (username: string): LogInAction => ({
  type: at.LOG_IN,
  payload: {
    username,
  },
});

type LogOutAction = FSA<typeof at.LOG_OUT, void>;

export const logOut = (): LogOutAction => ({
  type: at.LOG_OUT,
  payload: undefined,
});

export type SetApiUrlAction = FSA<
  typeof at.API_URL_SET,
  $ReadOnly<{|
    apiUrl: string,
  |}>,
>;

export const setApiUrl = (apiUrl: string): SetApiUrlAction => ({
  type: at.API_URL_SET,
  payload: {
    apiUrl,
  },
});

export type AddScannedProducts = FSA<
  typeof at.ADD_SCANNED_PRODUCTS,
  $ReadOnlyArray<Product>,
>;

export const addScannedProducts = (
  products: Product[],
): AddScannedProducts => ({
  type: at.ADD_SCANNED_PRODUCTS,
  payload: products,
});

export type ClearScans = FSA<typeof at.CLEAR_SCANS, void>;

export const clearScans = (): ClearScans => ({
  type: at.CLEAR_SCANS,
  payload: undefined,
});

export type RemoveScan = FSA<typeof at.REMOVE_SCAN, Scan>;

export const removeScan = (payload: Scan): RemoveScan => ({
  type: at.REMOVE_SCAN,
  payload,
});

export type AddScan = {
  type: typeof at.ADD_SCAN,
  payload: Scan,
};

export const addScan = (payload: Scan): AddScan => ({
  type: at.ADD_SCAN,
  payload,
});

export type ResolveScan = {
  type: typeof at.RESOLVE_SCAN,
  payload: $ReadOnly<{|
    scan: Scan,
    products: $ReadOnlyArray<Product>,
    catalogKey: string,
  |}>,
};

export const resolveScan = (
  scan: Scan,
  products: $ReadOnlyArray<Product>,
  catalogKey: string,
): ResolveScan => ({
  type: at.RESOLVE_SCAN,
  payload: {
    scan,
    products,
    catalogKey,
  },
});

export type UpdateScanWithCodes = {
  type: typeof at.UPDATE_SCAN_WITH_CODES,
  payload: $ReadOnly<{|
    scan: Scan,
    codes: $ReadOnlyArray<string>,
  |}>,
};

export const updateScanWithCodes = (
  scan: Scan,
  codes: $ReadOnlyArray<string>,
): UpdateScanWithCodes => ({
  type: at.UPDATE_SCAN_WITH_CODES,
  payload: {
    scan,
    codes,
  },
});

export const performScan =
  (
    code: string,
    customerNumber: string,
    catalog: AbstractCatalog,
    externalApi: boolean,
    usesProductLevelAts: boolean,
  ): ((dispatch: Dispatch) => any) =>
  (dispatch: Dispatch) => {
    const scan = makeScan(code);
    dispatch(addScan(scan));

    return externalApi
      ? dispatch(
          scanApi.keysByBarcode({
            scan,
            catalogKey: catalog.key,
            customerNumber,
          }),
        )
          .then((barcodes: BarcodeQuery[]) => barcodes.map(({ upc }) => upc))
          .then((keys: $ReadOnlyArray<string>) => {
            dispatch(updateScanWithCodes(scan, keys));
            return keys;
          })
          .then((keys: $ReadOnlyArray<string>) => ({ ...scan, keys }))
          .then((augmentedScan) =>
            augmentedScan.keys.length > 0
              ? dispatch(
                  scanApi.productsByScan({
                    scan: augmentedScan,
                    catalogKey: catalog.key,
                    customerNumber,
                  }),
                ).then((response: ProductsQueryResponse) => {
                  dispatch(
                    inventory.updateStockShipments(
                      response.stockShipments,
                      allocationStrategy(
                        usesProductLevelAts ? null : catalog.type,
                      ),
                      releaseDateFromStockRecord([catalog]),
                    ),
                  );

                  dispatch(resolveScan(scan, response.results, catalog.key));

                  return response;
                })
              : null,
          )
      : dispatch(
          scanApi.productsByScan({
            scan,
            catalogKey: catalog.key,
            customerNumber,
          }),
        ).then((response: ProductsQueryResponse) => {
          dispatch(
            inventory.updateStockShipments(
              response.stockShipments,
              allocationStrategy(usesProductLevelAts ? null : catalog.type),
              releaseDateFromStockRecord([catalog]),
            ),
          );

          dispatch(resolveScan(scan, response.results, catalog.key));

          return response;
        });
  };

export type CloseBarcodeScanning = FSA<typeof at.CLOSE_BARCODE_SCANNING, void>;

export const closeBarcodeScanning = (): CloseBarcodeScanning => ({
  type: at.CLOSE_BARCODE_SCANNING,
  payload: undefined,
});

type OpenBarcodeScanning = FSA<typeof at.OPEN_BARCODE_SCANNING, void>;

export const openBarcodeScanning = (): OpenBarcodeScanning => ({
  type: at.OPEN_BARCODE_SCANNING,
  payload: undefined,
});

type ClearExternalPrices = FSA<typeof at.EXTERNAL_PRICES_CLEAR, void>;

export const clearExternalPrices = (): ClearExternalPrices => ({
  type: at.EXTERNAL_PRICES_CLEAR,
  payload: undefined,
});

export const createCustomerSignup =
  (
    values: CustomerSignupFormValues,
  ): ((_dispatch: Dispatch, getState: GetState) => Promise<Response>) =>
  (_dispatch: Dispatch, getState: GetState): Promise<Response> =>
    customerSignupApi.createCustomerSignup(values, getState().apiUrl);

export const reportOrders = (
  values: ReportOrdersFormValues,
): ((dispatch: Dispatch, getState: GetState) => Bluebird$Promise<mixed>) =>
  reportOrdersApi.reportOrders(
    // $FlowFixMe[incompatible-call]
    (resources.reportOrders.serialized(values): any),
  );

export type Action =
  | SetApiUrlAction
  | LogOutAction
  | LogInAction
  | RequestResourceAction
  | ReceiveResourceAction
  | FailToReceiveResourceAction
  | AddScannedProducts
  | ClearScans
  | AddScan
  | RemoveScan
  | ResolveScan
  | UpdateScanWithCodes
  | OpenBarcodeScanning
  | CloseBarcodeScanning
  | ClearExternalPrices
  | NewOrderAction
  | QuikOrderHistoryAction
  | inventory.Action
  | SuperficallyUpdateUserIsDisabled;
